/**
 * Core game types for LEXIKON.
 *
 * The physical game uses 8 numbered boards (4×4 icons each), colored "frames"
 * to mark which icons belong to a sentence and which grammatical role they
 * play, plus small tokens for tense/gender/negation. These types model that
 * structure for the digital simulator.
 */

import type { Lang } from "../i18n";

export type FrameColor =
  | "verb"        // red
  | "noun"        // yellow
  | "adj"         // blue
  | "prep"        // green (prepositions + conjunctions)
  | "adverb"      // orange
  | "neutral";    // grey

export type Tense = "past" | "present" | "future";
export type Gender = "M" | "F" | "NB";

export type Column = "A" | "B" | "C" | "D";
export type Row = 1 | 2 | 3 | 4;

export interface IconRef {
  board: number;   // 1–8
  col: Column;
  row: Row;
}

/** Stable key for an icon, e.g. "1A2" or "7D4". */
export function iconKey(ref: IconRef): string {
  return `${ref.board}${ref.col}${ref.row}`;
}

export function parseIconKey(key: string): IconRef {
  const m = /^([1-8])([A-D])([1-4])$/.exec(key);
  if (!m) throw new Error(`Bad icon key: ${key}`);
  return { board: Number(m[1]), col: m[2] as Column, row: Number(m[3]) as Row };
}

/**
 * A single token placed on an icon as part of a sentence.
 * Tokens carry frame color (grammar role), plural marker, optional verb
 * tense, gender marker, and negation, plus their reading-order index.
 */
export interface SentenceToken {
  icon: IconRef;
  order: number;                 // 1-based position in the sentence
  frameColor: FrameColor;
  plural?: boolean;
  tense?: Tense;
  gender?: Gender;
  negation?: boolean;            // X token on the lower border
  compoundWithNext?: boolean;    // "+" join — read with the next token as one word
}

export type Translations = Record<Lang, string>;

export interface GlossaryEntry {
  iconKey: string;
  /** Per-language gloss for this icon as used in this code (matches the EN gloss table in the codebook). */
  gloss: Translations;
  pos: FrameColor;
  tense?: Tense;
  negation?: boolean;
  plural?: boolean;
  /** When this entry is part of a "+" compound, the compound result for the joined icons (e.g. Art+Man = Scientist). */
  compound?: Translations;
}

export interface CodeSentence {
  /** Two-digit code id, e.g. "01" … "32". */
  id: string;
  /** Number of icon tokens in the sentence (4–8 for the playtest selection). */
  iconCount: number;
  tokens: SentenceToken[];
  translations: Translations;
  glossary: GlossaryEntry[];
}

/* ----------------------------- Board metadata --------------------------- */

export interface BoardCellMeta {
  ref: IconRef;
  /**
   * Best-effort gloss for the icon, in each language. This is built from the
   * codebook glossary entries plus hand-authored fallbacks for cells not used
   * in any of the 32 selected codes. Some entries may be empty for now.
   */
  gloss: Partial<Translations>;
  /** True when the icon depicts two alternates (split cell). The X token then selects the X-marked image. */
  dualImage?: boolean;
}

export interface BoardMeta {
  id: number;        // 1–8
  /** Theme label per language (e.g. Persons, Actions). */
  theme: Translations;
  cells: BoardCellMeta[]; // 16 entries
}

/* --------------------------- Runtime game state ------------------------- */

export type GamePhase =
  | "lobby"
  | "compose"
  | "interpret"
  | "reveal"
  | "finished";

export interface Player {
  id: string;
  displayName: string;
  isBot?: boolean;
  score: number;
  joinedAt: number;
}

export interface Turn {
  index: number;              // 0-based turn index within the game
  composerId: string;
  mode: "code" | "invent";
  codeId?: string;            // when mode === 'code'
  sentence?: SentenceToken[]; // composed by the composer
  composerOwnTranslation?: string;
  composeStartAt?: number;
  interpretStartAt?: number;
  interpretEndAt?: number;
  interpretations: Record<string /* playerId */, string>;
  pointsAwarded?: Record<string /* playerId */, number>;
  revealedAt?: number;
}

export interface GameRoom {
  id: string;                 // doc id
  code: string;               // 6-letter human-friendly code
  hostId: string;
  language: Lang;
  phase: GamePhase;
  players: Record<string, Player>;
  turnOrder: string[];        // playerIds in turn order
  currentTurnIndex: number;
  rounds: number;             // total rounds (default 3 per player)
  scoreLimit: number;         // default 50
  /**
   * Seconds for the interpretation phase. null = no time limit (composer
   * reveals manually, or auto-reveals as soon as everyone submitted).
   */
  interpretSeconds: number | null;
  /** Scoring strictness — affects synonym acceptance + sentence-correct threshold. */
  strictness?: "strict" | "normal" | "easy";
  /** True when the host ended the game manually before all rounds completed. */
  endedEarly?: boolean;
  createdAt: number;
  updatedAt: number;
}
